// client/src/pages/seo/TageskarteDigitalErstellenDe.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "Eine Tageskarte sollte sich schnell und einfach aktualisieren lassen",
  },
  {
    id: "why",
    label: "Warum ist eine digitale Tageskarte sinnvoll?",
  },
  {
    id: "use-cases",
    label: "Wofür kann eine digitale Tageskarte genutzt werden?",
  },
  {
    id: "workflow",
    label: "Wie erstellt man eine digitale Tageskarte in der Praxis?",
  },
  {
    id: "content",
    label: "Was sollte eine gute Tageskarte enthalten?",
  },
  {
    id: "mistakes",
    label: "Welche Fehler passieren bei digitalen Tageskarten?",
  },
  {
    id: "recommendation",
    label: "Wie würde ich als Restaurantinhaber starten?",
  },
  {
    id: "next-steps",
    label: "Was sollten Sie jetzt tun?",
  },
  {
    id: "bestplace",
    label: "Wie Best Place hilft, eine digitale Tageskarte zu erstellen",
  },
  {
    id: "faq",
    label: "FAQ zu digitalen Tageskarten",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function TageskarteDigitalErstellenDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Tageskarte digital erstellen: Mittagsangebote und saisonale Gerichte | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/create-digital-daily-menu",
          de: "/de/tageskarte-digital-erstellen",
          no: "/no/opprett-digital-dagsmeny",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Tageskarte digital erstellen: Mittagsangebote, Wochenangebote und
              saisonale Gerichte online aktualisieren
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Eine Tageskarte funktioniert nur, wenn sie aktuell ist. Genau
                deshalb werden Papierkarten und PDFs schnell unpraktisch: Heute
                gibt es ein neues Mittagsangebot, morgen ist ein Gericht
                ausverkauft, nächste Woche startet ein saisonales Angebot.
              </p>

              <p className="seoHero__text">
                Eine digitale Tageskarte macht solche Änderungen einfacher. Das
                Restaurant kann Gerichte, Preise, Beschreibungen und Fotos online
                aktualisieren, während der QR-Code gleich bleibt.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            Eine Tageskarte sollte sich schnell und einfach aktualisieren lassen
          </h2>

          <p>
            Eine Standardspeisekarte kann monatelang gleich bleiben. Eine
            Tageskarte nicht. Sie reagiert auf Einkauf, Saison, Küchenkapazität,
            Nachfrage der Gäste und spontane Ideen des Teams.
          </p>

          <p>
            Deshalb sollte eine digitale Tageskarte nicht jedes Mal von einem
            Designer, einem neuen PDF-Export oder neuem Druckmaterial abhängen,
            wenn sich etwas ändert. Das Restaurant sollte aktuelle Angebote
            schnell aktualisieren können, während der QR-Code stabil bleibt.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Langsamer Ablauf</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Das Tagesgericht wird intern entschieden.
                </li>
                <li className="text__seo-li">
                  PDF oder Vorlage muss bearbeitet werden.
                </li>
                <li className="text__seo-li">
                  Eine neue Datei wird exportiert.
                </li>
                <li className="text__seo-li">
                  QR-Link oder Website wird geprüft.
                </li>
                <li className="text__seo-li">
                  Gäste sehen möglicherweise noch alte Angebote.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Besserer Ablauf</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Tagesgericht im Admin-Bereich hinzufügen.
                </li>
                <li className="text__seo-li">
                  Preis und kurze Beschreibung ergänzen.
                </li>
                <li className="text__seo-li">
                  Optional ein Foto hinzufügen.
                </li>
                <li className="text__seo-li">Änderungen speichern.</li>
                <li className="text__seo-li">
                  Die Tageskarte ist online aktualisiert.
                </li>
              </ul>
            </div>
          </div>

          <h3>Zuerst eine kleine Tageskarte testen</h3>

          <p>
            Fügen Sie drei bis fünf Tagesangebote hinzu, öffnen Sie die Karte auf
            einem Smartphone und prüfen Sie, ob der Ablauf für Ihr Team einfach
            genug ist. Ein kleiner Test ist meistens besser, als eine große
            Tageskarte aufzubauen, bevor klar ist, wie sie im täglichen Service
            funktioniert.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Warum ist eine digitale Tageskarte sinnvoll?</h2>

            <p>
              Tageskarten, Mittagsangebote und Wochenangebote ändern sich
              häufiger als die Hauptspeisekarte. Deshalb brauchen sie eine andere
              Art der Verwaltung.
            </p>

            <p>
              Wenn Gäste online ein Mittagsangebot sehen, das nicht mehr
              verfügbar ist, entsteht Enttäuschung. Wenn ein neues Angebot nicht
              sichtbar ist, verliert das Restaurant Aufmerksamkeit. Eine
              digitale Tageskarte hilft, solche Lücken zu vermeiden.
            </p>

            <h3>
              Tagesangebote brauchen schnellere Aktualisierungen als die
              Hauptspeisekarte
            </h3>

            <p>
              Ein Restaurant kann seine Hauptgerichte lange beibehalten, aber
              Tagesangebote hängen oft von Zutaten, Saison, Küchenplanung und
              Nachfrage ab. Je einfacher sie aktualisiert werden können, desto
              nützlicher werden sie.
            </p>

            <p>
              Das ist besonders relevant für Restaurants, die ihre Angebote
              mehrmals pro Woche ändern oder Mittagsangebote nutzen, um Gäste zu
              bestimmten Zeiten anzusprechen.
            </p>
          </div>
        </section>

        <section id="use-cases" className="acquaintance">
          <h2>Wofür kann eine digitale Tageskarte genutzt werden?</h2>

          <p>
            Eine digitale Tageskarte ist nicht nur für klassische
            Mittagsangebote gedacht. Sie kann für alle Inhalte genutzt werden,
            die sich häufig ändern oder nur für begrenzte Zeit verfügbar sind.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Mittagsangebote.</li>
            <li className="text__seo-li">Tagesgerichte.</li>
            <li className="text__seo-li">Wochenkarte.</li>
            <li className="text__seo-li">Saisonale Gerichte.</li>
            <li className="text__seo-li">Empfehlungen des Küchenchefs.</li>
            <li className="text__seo-li">Business Lunch.</li>
            <li className="text__seo-li">Zeitlich begrenzte Angebote.</li>
            <li className="text__seo-li">Ausverkaufte Produkte.</li>
          </ul>

          <p>
            Eine digitale Tageskarte ist besonders nützlich, wenn Angebote am
            Morgen, vor dem Mittagsservice oder im Laufe des Tages aktualisiert
            werden müssen.
          </p>

          <VisualIdea>
            Smartphone mit einer Tageskarte: heutige Empfehlung,
            Mittagsangebot, saisonales Gericht und ein ausverkaufter Artikel,
            der in der Ansicht ausgeblendet ist.
          </VisualIdea>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Wie erstellt man eine digitale Tageskarte in der Praxis?</h2>

            <p>
              Der Ablauf sollte so einfach sein, dass er nicht von einem
              Designer oder einer externen Agentur abhängt. Eine verantwortliche
              Person im Restaurant sollte die Tageskarte aktualisieren können.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Kategorie erstellen:</strong> Zum Beispiel „Tageskarte“,
                „Mittagsangebote“ oder „Heutige Empfehlungen“.
              </li>

              <li className="text__seo-li">
                <strong>Gerichte hinzufügen:</strong> Name, Preis und kurze
                Beschreibung ergänzen.
              </li>

              <li className="text__seo-li">
                <strong>Optional ein Foto hinzufügen:</strong> Bei neuen oder
                saisonalen Gerichten kann ein Bild helfen.
              </li>

              <li className="text__seo-li">
                <strong>Verfügbarkeit verwalten:</strong> Ausverkaufte Gerichte
                ausblenden oder ersetzen.
              </li>

              <li className="text__seo-li">
                <strong>Speichern und prüfen:</strong> Die öffentliche
                Speisekarte auf dem Smartphone öffnen.
              </li>
            </ol>

            <p>
              Der QR-Code muss nicht jeden Tag neu gedruckt werden. Er öffnet
              weiterhin die digitale Speisekarte, während die Inhalte dahinter
              aktualisiert werden.
            </p>
          </div>
        </section>

        <section id="content" className="acquaintance">
          <h2>Was sollte eine gute Tageskarte enthalten?</h2>

          <p>
            Eine Tageskarte sollte kurz, klar und leicht verständlich sein.
            Gäste möchten nicht lange suchen. Sie möchten schnell sehen, was
            heute besonders ist.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Klarer Titel.</li>
            <li className="text__seo-li">Kurzer Zeitraum.</li>
            <li className="text__seo-li">Name des Gerichts.</li>
            <li className="text__seo-li">Preis.</li>
            <li className="text__seo-li">Kurze Beschreibung.</li>
            <li className="text__seo-li">Foto, wenn es sinnvoll ist.</li>
            <li className="text__seo-li">Hinweis zur Verfügbarkeit.</li>
            <li className="text__seo-li">Getränk oder Menüoption.</li>
          </ul>

          <p>
            Tageskarten funktionieren oft am besten, wenn sie nicht überladen
            sind. Drei bis sieben starke Angebote können besser sein als eine
            lange, unklare Liste.
          </p>

          <h3>Eine Tageskarte sollte wie eine Empfehlung wirken</h3>

          <p>
            Das Ziel ist nicht, eine zweite vollständige Speisekarte zu
            erstellen. Das Ziel ist, Gästen zu zeigen, was sich heute besonders
            lohnt: ein Mittagsangebot, ein saisonales Gericht, eine Empfehlung
            des Küchenchefs oder ein begrenztes Angebot.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche Fehler passieren bei digitalen Tageskarten?</h2>

            <p>
              Eine digitale Tageskarte ist nur dann nützlich, wenn sie aktuell
              und klar bleibt. Sonst wird sie nur zu einer weiteren
              unübersichtlichen Menüseite.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Tagesgerichte bleiben mehrere Tage sichtbar.
                  </li>
                  <li className="text__seo-li">
                    Ausverkaufte Produkte werden nicht ausgeblendet.
                  </li>
                  <li className="text__seo-li">
                    Preise stimmen nicht mit Kasse oder gedruckter Karte überein.
                  </li>
                  <li className="text__seo-li">
                    Die Tageskarte wird zu lang.
                  </li>
                  <li className="text__seo-li">
                    Es gibt keinen klaren Titel.
                  </li>
                  <li className="text__seo-li">
                    Fotos wirken uneinheitlich oder ungeeignet.
                  </li>
                  <li className="text__seo-li">
                    Niemand ist für tägliche Aktualisierungen verantwortlich.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Zeitlich begrenzte Angebote klar kennzeichnen.
                  </li>
                  <li className="text__seo-li">
                    Ausverkaufte Gerichte sofort ausblenden.
                  </li>
                  <li className="text__seo-li">
                    Preise zentral prüfen.
                  </li>
                  <li className="text__seo-li">
                    Nur ausgewählte Tagesangebote zeigen.
                  </li>
                  <li className="text__seo-li">
                    Klare Titel wie „Heutige Empfehlungen“ nutzen.
                  </li>
                  <li className="text__seo-li">
                    Fotos bewusst auswählen.
                  </li>
                  <li className="text__seo-li">
                    Eine verantwortliche Person festlegen.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Wie würde ich als Restaurantinhaber starten?</h2>

          <p>
            Ich würde die Tageskarte am Anfang nicht zu groß machen. Ich würde
            mit einem kleinen Bereich starten, der sich tatsächlich regelmäßig
            ändert.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Kategorie „Heute“ erstellen:</strong> Diese Kategorie
              enthält nur aktuelle Angebote.
            </li>

            <li className="text__seo-li">
              <strong>Drei bis fünf Gerichte hinzufügen:</strong> Mit Preis und
              kurzer Beschreibung.
            </li>

            <li className="text__seo-li">
              <strong>Ein Foto testen:</strong> Nicht für jedes Gericht, sondern
              für das stärkste Angebot.
            </li>

            <li className="text__seo-li">
              <strong>QR-Code öffnen:</strong> Prüfen, wie die Tageskarte auf
              dem Smartphone aussieht.
            </li>

            <li className="text__seo-li">
              <strong>Eine Woche beobachten:</strong> Wird die Karte genutzt?
              Verweist das Team darauf?
            </li>

            <li className="text__seo-li">
              <strong>Später erweitern:</strong> Wochenkarte, saisonale Angebote
              oder Mittagsangebote nach dem ersten Test ergänzen.
            </li>
          </ol>

          <p>
            Dieser Ansatz hält die erste Version einfach und macht es leichter
            zu verstehen, ob eine digitale Tageskarte im täglichen Betrieb
            wirklich hilft.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was sollten Sie jetzt tun?</h2>

            <p>
              Wenn Sie eine digitale Tageskarte erstellen möchten, starten Sie
              mit einem kleinen Test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Eine digitale Speisekarte erstellen.</li>
              <li className="text__seo-li">
                Kategorie „Tageskarte“ hinzufügen.
              </li>
              <li className="text__seo-li">
                Drei bis fünf echte Angebote hinzufügen.
              </li>
              <li className="text__seo-li">
                Preise und kurze Beschreibungen ergänzen.
              </li>
              <li className="text__seo-li">
                Die Speisekarte auf dem Smartphone öffnen.
              </li>
              <li className="text__seo-li">
                Den QR-Code im Restaurant testen.
              </li>
              <li className="text__seo-li">
                Die Angebote nach einem Tag erneut aktualisieren.
              </li>
            </ul>

            <VisualIdea>
              Ein einfacher Tageskarten-Test: eine Kategorie, drei aktuelle
              Angebote, ein QR-Code und eine Smartphone-Ansicht, die die
              aktualisierten Gerichte zeigt.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ zu digitalen Tageskarten</h2>

          <div className="seoFaq">
            <details>
              <summary>Was ist eine digitale Tageskarte?</summary>
              <p>
                Das ist ein online verwalteter Bereich der Speisekarte, der
                aktuelle Tagesgerichte, Mittagsangebote, Wochenangebote oder
                saisonale Artikel zeigt.
              </p>
            </details>

            <details>
              <summary>Muss ich den QR-Code jeden Tag neu drucken?</summary>
              <p>
                Nein. Der QR-Code bleibt gleich, wenn er auf einen stabilen
                Menü-Link führt. Nur der Inhalt der Tageskarte wird aktualisiert.
              </p>
            </details>

            <details>
              <summary>Kann ich ausverkaufte Tagesgerichte ausblenden?</summary>
              <p>
                Ja. Das ist einer der wichtigsten Vorteile. Ein Gericht kann
                ausgeblendet und durch ein anderes Angebot ersetzt werden.
              </p>
            </details>

            <details>
              <summary>Wie viele Gerichte sollte eine Tageskarte haben?</summary>
              <p>
                Für einen ersten Test reichen oft drei bis sieben Angebote. Die
                Tageskarte sollte klar und leicht verständlich bleiben.
              </p>
            </details>

            <details>
              <summary>Ist eine digitale Tageskarte besser als eine PDF?</summary>
              <p>
                Wenn sich Angebote häufig ändern, ist eine digitale Tageskarte
                meistens praktischer, weil Inhalte direkt aktualisiert werden
                können.
              </p>
            </details>

            <details>
              <summary>Kann ich kostenlos starten?</summary>
              <p>
                Ja. Für einen ersten Test erstellen Sie einige Tagesangebote und
                prüfen die Ansicht über einen QR-Code auf dem Smartphone.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="menu"
          exclude="/de/tageskarte-digital-erstellen"
        />
      </article>
    </main>
  );
}